/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { FormattedMessage } from '@modules/i18n'

type MatchSummaryParams = {
  page: number
  currentPageSize: number
  shownCount: number | null
  matchCount: number | null
  pageSize: number | null
  totalPages: number | null
}

export function renderMatchSummary({
  page,
  currentPageSize,
  shownCount: rowCount,
  matchCount: totalCount,
  pageSize,
  totalPages,
}: MatchSummaryParams): React.ReactNode {
  if (!pageSize || !rowCount || rowCount <= pageSize) {
    if (totalCount === rowCount) {
      return null
    }

    const pagesInfo = totalPages
      ? ` (${totalPages} ${totalPages === 1 ? 'page' : 'pages'} total)`
      : ''
    return (
      <span data-test-id='match-underflowed'>
        <FormattedMessage
          id='deployment-list.match-underflowed-with-pages'
          defaultMessage='Showing { rowCount } matching {rowCount, plural, one {deployment} other {deployments}} out of a total of {totalCount} {totalCount, plural, one {deployment} other {deployments}}{pagesInfo}'
          values={{
            rowCount,
            totalCount,
            pagesInfo,
          }}
        />
      </span>
    )
  }

  if (rowCount === totalCount) {
    return (
      <FormattedMessage
        id='deployment-list.page-match-full-with-pages'
        defaultMessage='Showing page { page } with { currentPageSize } of { rowCount } {rowCount, plural, one {deployment} other {deployments}} (page {page} of {totalPages})'
        values={{
          page: page + 1,
          currentPageSize,
          rowCount,
          totalPages: totalPages || 1,
        }}
      />
    )
  }

  return (
    <span data-test-id='match-overflowed'>
      <FormattedMessage
        id='deployment-list.page-match-overflowed-with-pages'
        defaultMessage='Showing page { page } of { totalPages } with { currentPageSize } of { rowCount } matches out of a total of {totalCount} {totalCount, plural, one {deployment} other {deployments}}'
        values={{
          page: page + 1,
          totalPages: totalPages || 1,
          currentPageSize,
          rowCount,
          totalCount,
        }}
      />
    </span>
  )
}
